import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDocuments } from "../../hooks/useFileQuery";
import { useGetRoles } from "../../hooks/useReactQuery";
import LoadingAnimation from "../../components/loading/LoadingAnimation";
import { useLayout } from "../../context/LayoutContext";
import UserDocumentCard from "../../components/ui/cards/UserDocumentCard";
import UserDocumentRow from "../../components/ui/cards/UserDocumentRow";
import PreviewModal from "../../components/ui/modals/PreviewModal";
import _ from 'lodash';

// Iconos para el cambio de vista
import GridViewIcon from "../../assets/svg/view/grid.svg";
import GridViewLightIcon from "../../assets/svg/view/grid_light.svg";
import ListViewIcon from "../../assets/svg/view/list.svg";
import ListViewLightIcon from "../../assets/svg/view/list_light.svg";

import "../../assets/scss/views/DashboardHumanResource.scss";

// Función para calcular el estado de un documento con nueva lógica
const calculateDocumentStatus = (document, isRequired = false) => {
  if (!document.fileName && !document.id?.startsWith?.('temp-')) {
    return isRequired ? "missing-required" : "missing-optional";
  }
  
  if (!isRequired) return "not-required";
  if (!document.expirationDate) return "empty";

  const today = new Date();
  const expDate = new Date(document.expirationDate);
  const daysUntilExpiration = Math.ceil(
    (expDate - today) / (1000 * 60 * 60 * 24)
  );

  if (daysUntilExpiration < 0) return "expired";
  if (daysUntilExpiration <= 15) return "critical";
  if (daysUntilExpiration <= 30) return "warning";
  if (daysUntilExpiration <= 30) return "near-expiry";
  return "valid";
};

// Función para verificar si un documento cumple con los criterios de filtro temporal
const checkDocumentExpiryFilter = (document, filter) => {
  if (!document.expirationDate) return false;
  
  const today = new Date();
  const expDate = new Date(document.expirationDate);
  const daysUntilExpiration = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
  
  switch (filter) {
    case "expired":
      return daysUntilExpiration < 0;
    case "critical_15":
      return daysUntilExpiration >= 0 && daysUntilExpiration <= 15;
    case "warning_30":
      return daysUntilExpiration > 15 && daysUntilExpiration <= 30;
    case "expiry_1m":
      return daysUntilExpiration > 30 && daysUntilExpiration <= 30; // 1 mes aproximado
    default:
      return true;
  }
};

export default function DashboardHumanResource() {
  const { theme } = useLayout();
  const navigate = useNavigate();
  const sideNavRef = useRef(null);

  // Estados principales
  const [viewType, setViewType] = useState("cards");
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentDocumentType, setCurrentDocumentType] = useState("required"); // 'required' o 'additional'
  const [documentFilter, setDocumentFilter] = useState("all");
  
  // Estados para búsqueda con debounce
  const [searchInputValue, setSearchInputValue] = useState("");
  const [activeSearchFilter, setActiveSearchFilter] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  
  // Estado para paginación dinámica
  const [usersPerPage, setUsersPerPage] = useState(8);
  const [currentPage, setCurrentPage] = useState(1);

  // Estados para modales
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const [localFilters, setLocalFilters] = useState({
    roleId: null,
    documentFilter: "all",
    documentType: "required" // Nuevo filtro para tipo de documento
  });

  // Hook de documentos con filtros locales actualizados
  const {
    documents: users,
    pagination,
    isLoading,
    isError,
    filters: hookFilters,
    updateFilters,
    goToNextPage,
    goToPrevPage,
    goToPage,
  } = useDocuments({
    page: currentPage,
    limit: usersPerPage,
    search: activeSearchFilter,
    roleId: localFilters.roleId,
    documentFilter: localFilters.documentFilter,
    documentType: localFilters.documentType,
  });

  const { data: roles, isLoading: rolesLoading } = useGetRoles();
  const rolesData = roles?.data || [];

  // Calcular usuarios por página basado en altura del contenedor
  useEffect(() => {
    const calculateUsersPerPage = () => {
      if (sideNavRef.current) {
        const containerHeight = sideNavRef.current.clientHeight;
        const headerHeight = 60;
        const paginationHeight = 50;
        const userItemHeight = 80;
        const availableHeight = containerHeight - headerHeight - paginationHeight - 20;
        
        const calculatedUsers = Math.floor(availableHeight / userItemHeight);
        const newUsersPerPage = Math.max(5, Math.min(calculatedUsers, 15));
        
        if (newUsersPerPage !== usersPerPage) {
          setUsersPerPage(newUsersPerPage);
        }
      }
    };

    calculateUsersPerPage();
    window.addEventListener('resize', calculateUsersPerPage);
    
    return () => window.removeEventListener('resize', calculateUsersPerPage);
  }, [usersPerPage]);

  // Implementación de debounce mejorada
  const debouncedSearch = useCallback(
    _.debounce((searchValue) => {
      setActiveSearchFilter(searchValue);
      setIsSearching(false);
      setCurrentPage(1);
    }, 1000),
    []
  );

  // Handler de búsqueda mejorado
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setIsSearching(true);
    debouncedSearch(value);
  }, [debouncedSearch]);

  // Handler para limpiar búsqueda
  const handleClearSearch = useCallback(() => {
    setSearchInputValue("");
    setActiveSearchFilter("");
    setIsSearching(false);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  // Handler actualizado para filtros locales
  const handleFilterUpdate = useCallback((newFilters) => {
    setLocalFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    setCurrentPage(1); // Reset página al cambiar filtros
    if (updateFilters) {
      updateFilters({
        ...newFilters,
        page: 1
      });
    }
  }, [updateFilters]);

  // Handler para cambiar tipo de vista
  const handleViewTypeChange = useCallback(() => {
    setViewType(prevType => prevType === "cards" ? "linear" : "cards");
  }, []);

  // Handler para seleccionar usuario
  const handleUserSelect = useCallback((user) => {
    setSelectedUser(user);
    setDocumentFilter("all");
  }, []);

  // Handler para cambiar tipo de documento
  const handleDocumentTypeChange = useCallback((documentType) => {
    setCurrentDocumentType(documentType);
    handleFilterUpdate({ documentType });
  }, [handleFilterUpdate]);

  // Procesamiento de documentos del usuario seleccionado con nueva lógica
  const processedUserDocuments = useMemo(() => {
    if (!selectedUser) return { required: [], additional: [] };

    // Separar documentos basándose en is_additional del tipo de documento
    const required = selectedUser.documents.filter(doc => !doc.isAdditional);
    const additional = selectedUser.documents.filter(doc => doc.isAdditional);

    return { required, additional };
  }, [selectedUser]);

  // Filtrar documentos según filtro seleccionado y tipo de documento
  const filteredDocuments = useMemo(() => {
    if (!selectedUser) return [];

    let documentsToFilter = currentDocumentType === "additional" 
      ? processedUserDocuments.additional 
      : processedUserDocuments.required;

    return documentsToFilter.filter(doc => {
      const status = calculateDocumentStatus(doc, !doc.isAdditional);

      switch (documentFilter) {
        case "expired":
          return checkDocumentExpiryFilter(doc, "expired");
        case "critical_15":
          return checkDocumentExpiryFilter(doc, "critical_15");
        case "warning_30":
          return checkDocumentExpiryFilter(doc, "warning_30");
        case "expiry_1m":
          return checkDocumentExpiryFilter(doc, "expiry_1m");
        case "empty":
          return status === "empty" || status === "missing-required" || status === "missing-optional";
        case "required":
          return !doc.isAdditional;
        case "not-required":
          return doc.isAdditional;
        default:
          return true;
      }
    });
  }, [selectedUser, currentDocumentType, documentFilter, processedUserDocuments]);

  // Handlers para documentos
  const handlePreviewDocument = useCallback((doc) => {
    if (!doc.isEmpty && doc.fileName) {
      setSelectedDoc(doc);
      setOpenPreview(true);
    }
  }, []);

  const handleEditDocument = useCallback((userId) => {
    navigate(`/users/${userId}`);
  }, [navigate]);

  // Función para renderizar el icono según el tema
  const renderViewIcon = () => {
    if (viewType === "cards") {
      return theme === "dark" ? ListViewLightIcon : ListViewIcon;
    } else {
      return theme === "dark" ? GridViewLightIcon : GridViewIcon;
    }
  };

  // Estados de carga
  if (isLoading && !users.length) {
    return (
      <div className={`dashboard-loading theme-${theme}`}>
        <LoadingAnimation />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={`dashboard-error theme-${theme}`}>
        Error al cargar documentos
      </div>
    );
  }

  return (
    <div className={`dashboard-hr-container theme-${theme}`}>
      {/* Sistema de filtros actualizado */}
      <div className={`controls-section theme-${theme}`}>
        <div className="filters-container">
          <div className="filter-group">
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Buscar por nombre, email o documento..."
                value={searchInputValue}
                onChange={handleSearchChange}
                className={`search-input theme-${theme} ${isSearching ? 'searching' : ''}`}
              />
              {searchInputValue && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className={`clear-search-btn theme-${theme}`}
                  aria-label="Limpiar búsqueda"
                >
                  ×
                </button>
              )}
              {isSearching && (
                <div className={`search-loading-indicator theme-${theme}`}>
                  <span>Buscando...</span>
                </div>
              )}
            </div>
          </div>

          <div className="filter-group">
            <select
              onChange={(e) =>
                handleFilterUpdate({
                  roleId: e.target.value ? parseInt(e.target.value, 10) : null,
                })
              }
              value={localFilters.roleId || ""}
              className={`select-input theme-${theme}`}
              disabled={rolesLoading}
            >
              <option value="">Todos los roles</option>
              {rolesData.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <select
              onChange={(e) =>
                handleFilterUpdate({ documentFilter: e.target.value })
              }
              value={localFilters.documentFilter}
              className={`select-input theme-${theme}`}
            >
              <option value="all">Todos los documentos</option>
              <option value="expired">Documentos vencidos</option>
              <option value="critical_15">Documentos casi vencidos &lt; 15D</option>
              <option value="warning_30">Documentos a punto de vencer &lt; 30D</option>
              <option value="expiry_1m">Documentos entrando fecha de vencimiento 1M</option>
              <option value="empty">Documentos vacíos</option>
              <option value="required">Documentos obligatorios</option>
              <option value="additional">Documentos adicionales</option>
            </select>
          </div>

          <div className="filter-group">
            <select
              onChange={(e) => handleDocumentTypeChange(e.target.value)}
              value={currentDocumentType}
              className={`select-input theme-${theme}`}
            >
              <option value="required">Documentos Obligatorios</option>
              <option value="additional">Documentos Adicionales</option>
            </select>
          </div>
        </div>
      </div>

      {/* Layout principal */}
      <div className={`main-layout theme-${theme}`}>
        {/* Panel de usuarios */}
        <div className={`users-panel theme-${theme}`} ref={sideNavRef}>
          <div className="panel-header">
            <h3>
              Usuarios ({pagination.total || 0})
              {activeSearchFilter && (
                <span className="search-indicator">
                  - Búsqueda: "{activeSearchFilter}"
                </span>
              )}
            </h3>
          </div>
          
          <div className="users-list">
            {users.map((user) => (
              <div
                key={user.id}
                className={`user-item theme-${theme} ${
                  selectedUser?.id === user.id ? "selected" : ""
                }`}
                onClick={() => handleUserSelect(user)}
              >
                <div className="user-info">
                  <div className="user-name">{user.name}</div>
                  <div className="user-email">{user.email || "Sin email"}</div>
                  <div className="user-role">{user.role?.name || "Sin rol"}</div>
                </div>
                <div className="user-status">
                  <span className="doc-count">
                    {user.documents.filter(d => !d.isEmpty).length}/{user.documents.length} docs
                  </span>
                  <div className="document-type-indicators">
                    <span className="required-count">
                      Oblig: {user.documents.filter(d => !d.isAdditional && !d.isEmpty).length}
                    </span>
                    <span className="additional-count">
                      Adic: {user.documents.filter(d => d.isAdditional && !d.isEmpty).length}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Paginación en el panel */}
          {pagination.total > usersPerPage && (
            <div className={`panel-pagination theme-${theme}`}>
              <button
                onClick={goToPrevPage}
                disabled={pagination.currentPage === 1}
                className="pagination-btn"
              >
                ←
              </button>

              <span className="pagination-info">
                {pagination.currentPage} / {pagination.lastPage}
              </span>

              <button
                onClick={goToNextPage}
                disabled={pagination.currentPage === pagination.lastPage}
                className="pagination-btn"
              >
                →
              </button>
            </div>
          )}
        </div>

        {/* Panel de documentos */}
        <div className={`documents-panel theme-${theme}`}>
          {selectedUser ? (
            <>
              <div className="panel-header">
                <h3>
                  Documentos de {selectedUser.name} - 
                  {currentDocumentType === "additional" ? " Adicionales" : " Obligatorios"}
                </h3>
                
                <div className="header-actions">
                  {/* Botón de cambio de vista */}
                  <button
                    className={`view-toggle-btn theme-${theme}`}
                    onClick={handleViewTypeChange}
                    title={viewType === "cards" ? "Vista de lista" : "Vista de tarjetas"}
                  >
                    <img 
                      src={renderViewIcon()} 
                      alt={viewType === "cards" ? "Lista" : "Tarjetas"}
                    />
                  </button>
                </div>
              </div>

              <div className="document-filters">
                <div className="filters-row">
                  <select
                    onChange={(e) => setDocumentFilter(e.target.value)}
                    value={documentFilter}
                    className={`select-input theme-${theme}`}
                  >
                    <option value="all">Todos los documentos</option>
                    <option value="expired">Documentos vencidos</option>
                    <option value="critical_15">Críticos (≤15 días)</option>
                    <option value="warning_30">Próximos a vencer (≤30 días)</option>
                    <option value="expiry_1m">Fecha de vencimiento 1M</option>
                    <option value="empty">Sin archivos</option>
                  </select>

                  {/* Toggle para cambiar entre tipos de documentos */}
                  <div className={`document-type-toggle theme-${theme}`}>
                    <div className="toggle-buttons">
                      <button
                        className={`toggle-btn ${currentDocumentType === 'required' ? 'active' : ''}`}
                        onClick={() => handleDocumentTypeChange('required')}
                      >
                        Obligatorios ({processedUserDocuments.required.length})
                      </button>
                      <button
                        className={`toggle-btn ${currentDocumentType === 'additional' ? 'active' : ''}`}
                        onClick={() => handleDocumentTypeChange('additional')}
                      >
                        Adicionales ({processedUserDocuments.additional.length})
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className={viewType === "cards" ? "documents-grid" : "documents-list"}>
                {filteredDocuments.length > 0 ? (
                  filteredDocuments.map((doc) => {
                    const commonProps = {
                      key: doc.id || `${doc.documentTypeId}-${doc.documentTypeName}`,
                      document: doc,
                      theme: theme,
                      onPreview: () => handlePreviewDocument(doc),
                      onEdit: () => handleEditDocument(selectedUser.id),
                      status: calculateDocumentStatus(doc, !doc.isAdditional),
                      isRequired: !doc.isAdditional,
                      isEmpty: doc.isEmpty,
                      isExtraDocument: doc.isAdditional
                    };

                    return viewType === "cards" 
                      ? <UserDocumentCard {...commonProps} />
                      : <UserDocumentRow {...commonProps} />;
                  })
                ) : (
                  <div className="no-documents">
                    <p>
                      {currentDocumentType === "additional" 
                        ? "No hay documentos adicionales para mostrar" 
                        : "No hay documentos obligatorios que coincidan con los filtros seleccionados"
                      }
                    </p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-user-selected">
              <h3>Selecciona un usuario</h3>
              <p>Elige un usuario de la lista para ver sus documentos</p>
            </div>
          )}
        </div>
      </div>

      <PreviewModal
        open={openPreview}
        onClose={() => {
          setOpenPreview(false);
          setSelectedDoc(null);
        }}
        doc={selectedDoc}
        type="user"
      />
    </div>
  );
}